<template>
  <component :is="navbarFixed ? 'a-affix' : 'div'" :offset-top="top">
    <header
      class="dashboard-header"
      :class="[
        `dashboard-header--bg-${sidebarColorVal}`,
        `dashboard-header--text-${sidebarTextColorVal}`,
        `dashboard-header--selected-${sidebarSelectedTextColorVal}`,
      ]"
    >
      <div class="dashboard-header__top">
        <div class="dashboard-header__left">
          <button
            type="button"
            class="dashboard-header__toggle sidebar-toggler"
            aria-label="Toggle sidebar"
            @click="$emit('toggleSidebar', !sidebarCollapsed), resizeEventHandler()"
          >
            <i class="fas fa-bars" />
          </button>
        </div>

        <div class="dashboard-header__right header-control">
          <h-button type="link" class="dashboard-header__action">
            <SwitchLang />
          </h-button>
          <h-button
            type="link"
            class="dashboard-header__action"
            ref="secondarySidebarTriggerBtn"
            @click="$emit('toggleSettingsDrawer', true)"
          >
            <i class="fas fa-cog" />
          </h-button>
          <UserInform variant="dashboard" />
        </div>
      </div>

      <div class="dashboard-header__breadcrumb">
        <Breadcrumb />
      </div>
    </header>
  </component>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import SwitchLang from "@/components/lang/SwitchLang.vue";
import Breadcrumb from "@/components/breadcrumb/Breadcrumb.vue";
import UserInform from "@/layouts/dashboard/nav/user/UserInfor.vue";
import { HOME } from '@/const/path'
import commons from "@/utils/Common";

interface Props {
  navbarFixed: boolean;
  sidebarCollapsed: boolean;
  sidebarColor?: string;
  sidebarTextColor?: string;
  sidebarSelectedTextColor?: string;
  notificationsData?: any;
}

const props = withDefaults(defineProps<Props>(), {
  navbarFixed: false,
  sidebarCollapsed: false,
  sidebarColor: "primary",
  sidebarTextColor: "primary",
  sidebarSelectedTextColor: "white",
  notificationsData: () => [],
});

defineEmits(["toggleSidebar", "toggleSettingsDrawer"]);

const router = useRouter();
const top = ref(0);
const company = ref<Record<string, string>>({});

const sidebarColorVal = computed(() => props.sidebarColor || "primary");
const sidebarTextColorVal = computed(() => props.sidebarTextColor || "primary");
const sidebarSelectedTextColorVal = computed(
  () => props.sidebarSelectedTextColor || "white"
);

const companyName = computed(
  () => company.value.sub_name || company.value.subName || "KIMAHOME"
);
const companyTagline = computed(
  () => company.value.summary || ""
);
const companyLogo = computed(
  () => company.value.logo || company.value.urlLogo || ""
);

const resizeEventHandler = () => {
  top.value = top.value ? 0 : -0.01;
};

const goHome = () => {
  router.push(HOME);
};

const loadCompany = () => {
  const data = commons.getCokiesCompany();
  if (data) {
    company.value = data;
  }
};

onMounted(() => {
  loadCompany();
  window.addEventListener("resize", resizeEventHandler);
});
</script>

<style lang="scss" scoped>
.dashboard-header {
  /* Light → dark of dominant color (no gold) */
  --header-bg-soft: #3d6b9a;
  --header-bg-mid: #1e4a73;
  --header-bg-main: #102542;
  --header-bg-deep: #0a1829;
  --header-accent-dark: #102542;
  --header-shadow: 0 4px 10px rgba(16, 37, 66, 0.18), 0 2px 4px rgba(16, 37, 66, 0.08);
  --header-text-color: #ffffff;
  --header-text-muted: rgba(255, 255, 255, 0.72);
  --header-selected-text-color: #ffffff;
  margin: 0;
  background: var(--bg-white, #fff);
  border-bottom: none;
}

/* Background from Sidebar Color — brighter left, deeper right */
.dashboard-header--bg-primary {
  --header-bg-soft: #3d6b9a;
  --header-bg-mid: #1e4a73;
  --header-bg-main: #102542;
  --header-bg-deep: #0a1829;
  --header-accent-dark: #102542;
  --header-shadow: 0 4px 10px rgba(16, 37, 66, 0.18), 0 2px 4px rgba(16, 37, 66, 0.08);
}

.dashboard-header--bg-secondary {
  --header-bg-soft: #b37feb;
  --header-bg-mid: #6b3fa0;
  --header-bg-main: #4b2a78;
  --header-bg-deep: #2a1840;
  --header-accent-dark: #4b2a78;
  --header-shadow: 0 4px 10px rgba(75, 42, 120, 0.2), 0 2px 4px rgba(75, 42, 120, 0.1);
}

.dashboard-header--bg-success {
  --header-bg-soft: #8bc34a;
  --header-bg-mid: #558b2f;
  --header-bg-main: #2e7d32;
  --header-bg-deep: #1b5e20;
  --header-accent-dark: #1b5e2f;
  --header-shadow: 0 4px 10px rgba(46, 125, 50, 0.2), 0 2px 4px rgba(46, 125, 50, 0.1);
}

.dashboard-header--bg-warning {
  --header-bg-soft: #d4a84b;
  --header-bg-mid: #8a6f28;
  --header-bg-main: #6b5610;
  --header-bg-deep: #2a2208;
  --header-accent-dark: #6b5610;
  --header-shadow: 0 4px 10px rgba(107, 86, 16, 0.2), 0 2px 4px rgba(107, 86, 16, 0.1);
}

.dashboard-header--bg-danger {
  --header-bg-soft: #ef5350;
  --header-bg-mid: #c0392b;
  --header-bg-main: #7a1520;
  --header-bg-deep: #2a0c10;
  --header-accent-dark: #7a1520;
  --header-shadow: 0 4px 10px rgba(122, 21, 32, 0.2), 0 2px 4px rgba(122, 21, 32, 0.1);
}

.dashboard-header--bg-black {
  --header-bg-soft: #595959;
  --header-bg-mid: #262626;
  --header-bg-main: #141414;
  --header-bg-deep: #0a0a0a;
  --header-accent-dark: #141414;
  --header-shadow: 0 4px 10px rgba(0, 0, 0, 0.22), 0 2px 4px rgba(0, 0, 0, 0.12);
}

/* Header text color */
.dashboard-header--text-primary {
  --header-text-color: #b19663;
  --header-text-muted: rgba(177, 150, 99, 0.72);
}

.dashboard-header--text-white {
  --header-text-color: #ffffff;
  --header-text-muted: rgba(255, 255, 255, 0.72);
}

.dashboard-header--text-secondary {
  --header-text-color: #b37feb;
  --header-text-muted: rgba(179, 127, 235, 0.75);
}

.dashboard-header--text-success {
  --header-text-color: #52c41a;
  --header-text-muted: rgba(82, 196, 26, 0.75);
}

.dashboard-header--text-warning {
  --header-text-color: #fadb14;
  --header-text-muted: rgba(250, 219, 20, 0.78);
}

.dashboard-header--text-danger {
  --header-text-color: #f5222d;
  --header-text-muted: rgba(245, 34, 45, 0.75);
}

.dashboard-header--text-black {
  --header-text-color: #141414;
  --header-text-muted: rgba(20, 20, 20, 0.7);
}

/* Selected / hover text color */
.dashboard-header--selected-primary {
  --header-selected-text-color: #b19663;
}

.dashboard-header--selected-white {
  --header-selected-text-color: #ffffff;
}

.dashboard-header--selected-secondary {
  --header-selected-text-color: #b37feb;
}

.dashboard-header--selected-success {
  --header-selected-text-color: #52c41a;
}

.dashboard-header--selected-warning {
  --header-selected-text-color: #fadb14;
}

.dashboard-header--selected-danger {
  --header-selected-text-color: #f5222d;
}

.dashboard-header--selected-black {
  --header-selected-text-color: #141414;
}

.dashboard-header__top {
  position: relative;
  display: flex;
  align-items: stretch;
  min-height: 52px;
  padding: 0;
  overflow: visible;
  background: linear-gradient(
    90deg,
    var(--header-bg-soft, #3d6b9a) 0%,
    var(--header-bg-mid, #1e4a73) 32%,
    var(--header-bg-main, #102542) 68%,
    var(--header-bg-deep, #0a1829) 100%
  );
  border-bottom: none;
  box-shadow: var(
    --header-shadow,
    0 4px 10px rgba(16, 37, 66, 0.18),
    0 2px 4px rgba(16, 37, 66, 0.08)
  );
}

.dashboard-header__left {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
  min-width: 72px;
  padding: 0 36px 0 16px;
  background: #fff;
  /* White chevron cut into the gradient (same ratio as reference) */
  clip-path: polygon(0 0, calc(100% - 22px) 0, 100% 50%, calc(100% - 22px) 100%, 0 100%);
}

.dashboard-header__logo {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  cursor: pointer;
}

.dashboard-header__logo-img {
  display: block;
  height: 36px;
  width: auto;
  max-width: 180px;
  object-fit: contain;
}

.dashboard-header__brand {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.1;
}

.dashboard-header__logo-text {
  font-family: var(--heading-font, "libre-baskerville", serif);
  font-size: 22px;
  font-weight: 700;
  color: var(--header-accent-dark, var(--primary-dark, #102542));
  letter-spacing: 0.12em;
  text-transform: uppercase;
  white-space: nowrap;
}

.dashboard-header__logo-tagline {
  margin-top: 4px;
  font-family: var(--nav-font, "libre-baskerville", serif);
  font-size: 10px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--header-text-color, #fff);
  white-space: nowrap;
}

.dashboard-header__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--header-accent-dark, var(--primary-dark, #102542));
  font-size: 18px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: color-mix(in srgb, var(--header-bg-main, #102542) 10%, #fff);
    color: var(--header-bg-main, #102542);
  }
}

.dashboard-header__right {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  padding: 0 20px;
  min-width: 0;
}

.dashboard-header__action {
  height: auto;
  padding: 0 8px;
  margin: 0;
  box-shadow: none;
  color: var(--header-text-color, var(--primary-gold, #b19663)) !important;

  i {
    font-size: 1.1rem;
    color: var(--header-text-color, var(--primary-gold, #b19663));
  }

  &:hover {
    color: var(--header-selected-text-color, #fff) !important;

    i {
      color: var(--header-selected-text-color, #fff);
    }
  }
}

.dashboard-header__right :deep(.menu-nav) {
  display: flex;
  align-items: center;
}

.dashboard-header__right :deep(.user-dropdown-link) {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.dashboard-header__right :deep(.user-name) {
  padding: 0 !important;
  margin: 0 !important;
  color: var(--header-text-color, var(--primary-gold, #b19663)) !important;
  font-family: var(--nav-font, "libre-baskerville", serif);
  font-size: 14px;
  font-weight: 500;
  background: none !important;
}

.dashboard-header__right :deep(.ant-avatar) {
  background: color-mix(in srgb, var(--header-text-color, #b19663) 25%, transparent);
  border: 1px solid color-mix(in srgb, var(--header-text-color, #b19663) 50%, transparent);
  color: var(--header-text-color, var(--primary-gold, #b19663));
}

.dashboard-header__right :deep(.dashboard-user-chevron) {
  color: var(--header-text-color, var(--primary-gold, #b19663));
  font-size: 12px;
  opacity: 0.9;
}

.dashboard-header__right :deep(.language-selector) {
  color: var(--header-text-color, var(--primary-gold, #b19663));
  font-family: var(--nav-font, "libre-baskerville", serif);
  font-size: 12px;
  letter-spacing: 0.04em;
  cursor: pointer;

  span {
    color: var(--header-text-color, var(--primary-gold, #b19663));
  }

  .bold {
    color: var(--header-selected-text-color, #fff);
    font-weight: 600;
  }
}

.dashboard-header__breadcrumb {
  padding: 12px 20px 8px;
  background: transparent;
  border-top: 1px solid var(--border-light, #e5e5e5);
}

.dashboard-header__breadcrumb :deep(.break-crumb) {
  padding-top: 0 !important;
}

@media (max-width: 767px) {
  .dashboard-header__left {
    min-width: 60px;
    padding: 0 28px 0 12px;
  }

  .dashboard-header__right {
    padding: 0 12px;
    gap: 0;
  }

  .dashboard-header__right :deep(.user-name),
  .dashboard-header__logo-tagline {
    display: none;
  }

  .dashboard-header__logo-text {
    font-size: 16px;
    letter-spacing: 0.08em;
  }

  .dashboard-header__breadcrumb {
    padding: 10px 12px 6px;
  }
}
</style>

<style>
.dark-mode .dashboard-header__right .dashboard-header__action,
.dark-mode .dashboard-header__right .dashboard-header__action i {
  color: var(--header-text-color, var(--default-header-color, #b19663)) !important;
}
</style>
